import {
  type IPortfolioTransactionItem,
  ITransactionType,
} from '~/@api/generated-api-types-account/Api'
import type { ObjectValues } from '~/@lib/utils/type.utils'

export type Transaction = IPortfolioTransactionItem

export type TransactionType = ObjectValues<typeof ITransactionType>

// These are the payments from project owner to investor
// PAYOUT_BONUS_WELCOME does NOT belong here as it is not counted towards the investments progress
export const InvestmentPayoutTransactionTypes = [
  ITransactionType.PayoutRevenue,
  ITransactionType.PayoutRevenue2,
  ITransactionType.PayoutBonus,
  ITransactionType.PayoutBonusFixed,
  ITransactionType.PayoutPrincipal,
] as TransactionType[]

export const InvestorPayoutFeeTransactionTypes = [
  ITransactionType.PayoutRevenue,
  ITransactionType.PayoutRevenue2,
  ITransactionType.PayoutBonus,
  ITransactionType.PayoutBonusFixed,
] as Array<TransactionType>

export const InvestorRelevantTransactionTypes = [
  ...InvestmentPayoutTransactionTypes,
  ITransactionType.Credit,
  ITransactionType.Withdrawal,
  ITransactionType.Reservation,
  ITransactionType.Investment,
  ITransactionType.Gift,
  ITransactionType.PayoutBonusWelcome,
  ITransactionType.PayoutVipBonus,
  ITransactionType.BonusAffiliate,
  ITransactionType.InvestmentRevenueFee,
  ITransactionType.InvestmentTransferOut,
  ITransactionType.InvestmentTransferIn,
  ITransactionType.DispoLegacyIn,
  ITransactionType.DispoLegacyOut,
] as TransactionType[]

export const TRANSACTION_TYPE_CREDIT = ITransactionType.Credit
export const TRANSACTION_TYPE_GIFT = ITransactionType.Gift
export const TRANSACTION_TYPE_RESERVATION = ITransactionType.Reservation
export const TRANSACTION_TYPE_INVESTMENT = ITransactionType.Investment
export const TRANSACTION_TYPE_REVENUE = ITransactionType.Revenue
export const TRANSACTION_TYPE_PAYOUT_REVENUE = ITransactionType.PayoutRevenue
export const TRANSACTION_TYPE_PAYOUT_REVENUE2 = ITransactionType.PayoutRevenue2
export const TRANSACTION_TYPE_PAYOUT_BONUS = ITransactionType.PayoutBonus
export const TRANSACTION_TYPE_PAYOUT_BONUS_FIXED =
  ITransactionType.PayoutBonusFixed
export const TRANSACTION_TYPE_PAYOUT_VIP_BONUS = ITransactionType.PayoutVipBonus
export const TRANSACTION_TYPE_PAYOUT_BONUS_WELCOME =
  ITransactionType.PayoutBonusWelcome
export const TRANSACTION_TYPE_PAYOUT_PRINCIPAL =
  ITransactionType.PayoutPrincipal
export const TRANSACTION_TYPE_WITHDRAVAL = ITransactionType.Withdrawal
export const TRANSACTION_TYPE_BONUS_PRESERVATION =
  ITransactionType.BonusPrereservation
export const TRANSACTION_TYPE_BONUS_LASTMINUTE =
  ITransactionType.BonusLastminute
export const TRANSACTION_TYPE_BONUS_PRINCIPAL = ITransactionType.BonusPrincipal
export const TRANSACTION_TYPE_BONUS_AFFILIATE = ITransactionType.BonusAffiliate
export const TRANSACTION_TYPE_BONUS_WELCOME = ITransactionType.BonusWelcome
export const TRANSACTION_TYPE_WITHDRAWAL_PRINCIPAL =
  ITransactionType.WithdrawalPrincipal
export const TRANSACTION_TYPE_LOAN_GRANTING = ITransactionType.LoanGranting
export const TRANSACTION_TYPE_LOAN_PAYMENT = ITransactionType.LoanPayment
export const TRANSACTION_TYPE_GIFT_PAYMENT = ITransactionType.GiftPayment
export const TRANSACTION_TYPE_RETURN_RESERVED = ITransactionType.ReturnReserved
export const TRANSACTION_TYPE_MARKETPLACE_SOLD =
  ITransactionType.MarketplaceSold
export const TRANSACTION_TYPE_MARKETPLACE_BOUGHT =
  ITransactionType.MarketplaceBought
export const TRANSACTION_TYPE_MARKETPLACE_RESERVATION =
  ITransactionType.MarketplaceReservation
export const TRANSACTION_TYPE_MARKETPLACE_FEE = ITransactionType.MarketplaceFee
export const TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE =
  ITransactionType.InvestmentRevenueFee
export const TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE_PAYMENT =
  ITransactionType.InvestmentRevenueFeePayment
export const TRANSACTION_TYPE_INVESTMENT_TRANSFER_OUT =
  ITransactionType.InvestmentTransferOut
export const TRANSACTION_TYPE_INVESTMENT_TRANSFER_IN =
  ITransactionType.InvestmentTransferIn
export const TRANSACTION_TYPE_DISPO_LEGACY_IN = ITransactionType.DispoLegacyIn
export const TRANSACTION_TYPE_DISPO_LEGACY_OUT = ITransactionType.DispoLegacyOut

export const TRANSACTION_BALANCE_PLUS = '+'
export const TRANSACTION_BALANCE_MINUS = '-'
export const TRANSACTION_BALANCE_REMAIN = null

const transactionTypeLabels = {
  [TRANSACTION_TYPE_CREDIT]: 'kredit',
  [TRANSACTION_TYPE_RESERVATION]: 'rezervácia',
  [TRANSACTION_TYPE_INVESTMENT]: 'investícia',
  [TRANSACTION_TYPE_REVENUE]: 'príjem',
  [TRANSACTION_TYPE_PAYOUT_REVENUE]: 'príjem výnosu',
  [TRANSACTION_TYPE_PAYOUT_REVENUE2]: 'príjem výnosu v predĺžení',
  [TRANSACTION_TYPE_PAYOUT_BONUS]: 'príjem bonusu',
  [TRANSACTION_TYPE_PAYOUT_BONUS_FIXED]: 'príjem kap. rastu',
  [TRANSACTION_TYPE_PAYOUT_PRINCIPAL]: 'príjem istiny',
  [TRANSACTION_TYPE_WITHDRAVAL]: 'výber',
  [TRANSACTION_TYPE_LOAN_PAYMENT]: 'splátka pôžičky',
  [TRANSACTION_TYPE_GIFT_PAYMENT]: 'výplata darčeka',
  [TRANSACTION_TYPE_RETURN_RESERVED]: 'rezervácia zrušená',
  [TRANSACTION_TYPE_MARKETPLACE_BOUGHT]: 'kúpa investície',
  [TRANSACTION_TYPE_MARKETPLACE_SOLD]: 'predaj investície',
  [TRANSACTION_TYPE_MARKETPLACE_RESERVATION]: 'rezervácia marketplace',
  [TRANSACTION_TYPE_MARKETPLACE_FEE]: 'poplatok marketplace',
  [TRANSACTION_TYPE_GIFT]: 'darček',
  [TRANSACTION_TYPE_INVESTMENT_TRANSFER_OUT]:
    'Postúpenie investície - odovzdanie',
  [TRANSACTION_TYPE_INVESTMENT_TRANSFER_IN]:
    'Postúpenie investície - prevzatie',
  [TRANSACTION_TYPE_PAYOUT_BONUS_WELCOME]: 'príjem welcome bonusu',
  [TRANSACTION_TYPE_PAYOUT_VIP_BONUS]: 'príjem VIP bonusu',
  [TRANSACTION_TYPE_BONUS_PRESERVATION]: 'predrezervačný bonus',
  [TRANSACTION_TYPE_BONUS_LASTMINUTE]: 'lastminute bonus',
  [TRANSACTION_TYPE_BONUS_PRINCIPAL]: 'bonus z istiny',
  [TRANSACTION_TYPE_BONUS_AFFILIATE]: 'affiliate bonus',
  [TRANSACTION_TYPE_BONUS_WELCOME]: 'welcome bonus',
  [TRANSACTION_TYPE_WITHDRAWAL_PRINCIPAL]: 'výber istiny',
  [TRANSACTION_TYPE_LOAN_GRANTING]: 'poskytnutie pôžičky',
  [TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE]: 'poplatok za výnos',
  [TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE_PAYMENT]: 'príjem poplatku za výnos',
  [TRANSACTION_TYPE_DISPO_LEGACY_IN]:
    'pripísanie disponibilných prostriedkov z pôvodného systému',
  [TRANSACTION_TYPE_DISPO_LEGACY_OUT]:
    'odpis disponibilných prostriedkov z pôvodného systému',
}

type TransactionTypeArrayItem = {
  value: TransactionType
  text: string
  balance: string | null
}

const TransactionTypesArray = (): Array<TransactionTypeArrayItem> => {
  return [
    // Kredit (+)
    {
      value: TRANSACTION_TYPE_CREDIT,
      text: transactionTypeLabels[TRANSACTION_TYPE_CREDIT],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Dar (+)
    {
      value: TRANSACTION_TYPE_GIFT,
      text: transactionTypeLabels[TRANSACTION_TYPE_GIFT],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Rezervacia (-)
    {
      value: TRANSACTION_TYPE_RESERVATION,
      text: transactionTypeLabels[TRANSACTION_TYPE_RESERVATION],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Investicia
    {
      value: TRANSACTION_TYPE_INVESTMENT,
      text: transactionTypeLabels[TRANSACTION_TYPE_INVESTMENT],
      balance: TRANSACTION_BALANCE_REMAIN,
    },
    // Výnos (+)
    {
      value: TRANSACTION_TYPE_REVENUE,
      text: transactionTypeLabels[TRANSACTION_TYPE_REVENUE],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výplata - primárny výnos (+)
    {
      value: TRANSACTION_TYPE_PAYOUT_REVENUE,
      text: transactionTypeLabels[TRANSACTION_TYPE_PAYOUT_REVENUE],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výplata - sekundárny výnos (+)
    {
      value: TRANSACTION_TYPE_PAYOUT_REVENUE2,
      text: transactionTypeLabels[TRANSACTION_TYPE_PAYOUT_REVENUE2],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výplata - bonus (+)
    {
      value: TRANSACTION_TYPE_PAYOUT_BONUS,
      text: transactionTypeLabels[TRANSACTION_TYPE_PAYOUT_BONUS],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výplata - bonus (+)
    {
      value: TRANSACTION_TYPE_PAYOUT_BONUS_WELCOME,
      text: transactionTypeLabels[TRANSACTION_TYPE_PAYOUT_BONUS_WELCOME],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výplata - bonus (+)
    {
      value: TRANSACTION_TYPE_PAYOUT_VIP_BONUS,
      text: transactionTypeLabels[TRANSACTION_TYPE_PAYOUT_VIP_BONUS],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výplata - fixed bonus - kap. rast (+)
    {
      value: TRANSACTION_TYPE_PAYOUT_BONUS_FIXED,
      text: transactionTypeLabels[TRANSACTION_TYPE_PAYOUT_BONUS_FIXED],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výplata - istina (+)
    {
      value: TRANSACTION_TYPE_PAYOUT_PRINCIPAL,
      text: transactionTypeLabels[TRANSACTION_TYPE_PAYOUT_PRINCIPAL],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Výber (-)
    {
      value: TRANSACTION_TYPE_WITHDRAVAL,
      text: transactionTypeLabels[TRANSACTION_TYPE_WITHDRAVAL],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Predrezervačný bonus
    {
      value: TRANSACTION_TYPE_BONUS_PRESERVATION,
      text: transactionTypeLabels[TRANSACTION_TYPE_BONUS_PRESERVATION],
      balance: TRANSACTION_BALANCE_REMAIN,
    },
    // Lastminute bonus
    {
      value: TRANSACTION_TYPE_BONUS_LASTMINUTE,
      text: transactionTypeLabels[TRANSACTION_TYPE_BONUS_LASTMINUTE],
      balance: TRANSACTION_BALANCE_REMAIN,
    },
    // Bonus z istiny
    {
      value: TRANSACTION_TYPE_BONUS_PRINCIPAL,
      text: transactionTypeLabels[TRANSACTION_TYPE_BONUS_PRINCIPAL],
      balance: TRANSACTION_BALANCE_REMAIN,
    },
    // Affiliate bonus (+)
    {
      value: TRANSACTION_TYPE_BONUS_AFFILIATE,
      text: transactionTypeLabels[TRANSACTION_TYPE_BONUS_AFFILIATE],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Welcome bonus
    {
      value: TRANSACTION_TYPE_BONUS_WELCOME,
      text: transactionTypeLabels[TRANSACTION_TYPE_BONUS_WELCOME],
      balance: TRANSACTION_BALANCE_REMAIN,
    },
    // Výber istiny (-)
    {
      value: TRANSACTION_TYPE_WITHDRAWAL_PRINCIPAL,
      text: transactionTypeLabels[TRANSACTION_TYPE_WITHDRAWAL_PRINCIPAL],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Poskytnutie pôžičky (+)
    {
      value: TRANSACTION_TYPE_LOAN_GRANTING,
      text: transactionTypeLabels[TRANSACTION_TYPE_LOAN_GRANTING],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Platba pôžičky (-)
    {
      value: TRANSACTION_TYPE_LOAN_PAYMENT,
      text: transactionTypeLabels[TRANSACTION_TYPE_LOAN_PAYMENT],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Platba darčeku (-)
    {
      value: TRANSACTION_TYPE_GIFT_PAYMENT,
      text: transactionTypeLabels[TRANSACTION_TYPE_GIFT_PAYMENT],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Vrátenie rezervovaných prostriedkov (+)
    {
      value: TRANSACTION_TYPE_RETURN_RESERVED,
      text: transactionTypeLabels[TRANSACTION_TYPE_RETURN_RESERVED],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Marketplace sold (+)
    {
      value: TRANSACTION_TYPE_MARKETPLACE_SOLD,
      text: transactionTypeLabels[TRANSACTION_TYPE_MARKETPLACE_SOLD],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Marketplace bought (-)
    {
      value: TRANSACTION_TYPE_MARKETPLACE_BOUGHT,
      text: transactionTypeLabels[TRANSACTION_TYPE_MARKETPLACE_BOUGHT],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Marketplace offer (-)
    {
      value: TRANSACTION_TYPE_MARKETPLACE_RESERVATION,
      text: transactionTypeLabels[TRANSACTION_TYPE_MARKETPLACE_RESERVATION],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Marketplace commission (-)
    {
      value: TRANSACTION_TYPE_MARKETPLACE_FEE,
      text: transactionTypeLabels[TRANSACTION_TYPE_MARKETPLACE_FEE],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Investment revenue fee (-)
    {
      value: TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE,
      text: transactionTypeLabels[TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE],
      balance: TRANSACTION_BALANCE_MINUS,
    },
    // Investment revenue fee payment (+)
    {
      value: TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE_PAYMENT,
      text: transactionTypeLabels[
        TRANSACTION_TYPE_INVESTMENT_REVENUE_FEE_PAYMENT
      ],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Investment transfer out ()
    {
      value: TRANSACTION_TYPE_INVESTMENT_TRANSFER_OUT,
      text: transactionTypeLabels[TRANSACTION_TYPE_INVESTMENT_TRANSFER_OUT],
      balance: TRANSACTION_BALANCE_REMAIN,
    },
    // Investment transfer in ()
    {
      value: TRANSACTION_TYPE_INVESTMENT_TRANSFER_IN,
      text: transactionTypeLabels[TRANSACTION_TYPE_INVESTMENT_TRANSFER_IN],
      balance: TRANSACTION_BALANCE_REMAIN,
    },
    // Pripísanie dispa z pôvodného systému (+)
    {
      value: TRANSACTION_TYPE_DISPO_LEGACY_IN,
      text: transactionTypeLabels[TRANSACTION_TYPE_DISPO_LEGACY_IN],
      balance: TRANSACTION_BALANCE_PLUS,
    },
    // Odpis dispa z pôvodného systému (-), vzniká len v pôvodnom systéme
    {
      value: TRANSACTION_TYPE_DISPO_LEGACY_OUT,
      text: transactionTypeLabels[TRANSACTION_TYPE_DISPO_LEGACY_OUT],
      balance: TRANSACTION_BALANCE_MINUS,
    },
  ]
}

export const InvestorRelevantTransactionTypesArray: Array<TransactionTypeArrayItem> =
  TransactionTypesArray().filter((transactionType) =>
    InvestorRelevantTransactionTypes.some(
      (investorRelevantTransaction) =>
        investorRelevantTransaction === transactionType.value
    )
  )

export { TransactionTypesArray }
